import React, { useState } from "react";
import AsyncSelect from "react-select/async";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useSelector } from "react-redux";
import { useTransferSearch } from "../../../../context/TransferSearchContext";
import { getPostalCode, searchLocations, searchTransfers, formatApiError } from "../../../../services/TransferService";
import QuantityStepper from "./QuantityStepper";
import { isBookableDate } from "../../../../utils/booking";

const selectStyles = {
	control: (provided, state) => ({
		...provided,
		minHeight: "44px",
		borderColor: state.isFocused ? "#B0B0B0" : provided.borderColor,
		boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(108, 117, 125, 0.25)" : provided.boxShadow,
		"&:hover": { borderColor: state.isFocused ? "#B0B0B0" : provided.borderColor },
	}),
	// Without a portal this menu renders inline and gets tangled up with the
	// neighbouring field's dropdown (low default z-index, no explicit background) —
	// same class of bug we fixed the same way on the admin side.
	menuPortal: (provided) => ({ ...provided, zIndex: 1000000 }),
	menu: (provided) => ({ ...provided, backgroundColor: "#fff", opacity: 1 }),
};

function combineDateAndTime(date, time) {
	if (!date) return null;
	const combined = new Date(date);
	combined.setHours(Number.isInteger(time?.hour) ? time.hour : 0);
	combined.setMinutes(Number.isInteger(time?.minute) ? time.minute : 0);
	combined.setSeconds(0);
	combined.setMilliseconds(0);
	return combined;
}

// search_bar.php calls Google Places server-side and returns everything it finds
// worldwide — there's no country-restriction param exposed to filter that call
// itself, so this filters its results client-side instead. Landmarks sourced from
// flymiles' own database ("flymiles") are already scoped to where you operate and
// are left untouched; only "google-places" results are checked against AU/NZ.
const OPERATING_COUNTRIES = ["australia", "new zealand"];

function isInOperatingCountry(landmark) {
	const lower = (landmark || "").toLowerCase();
	return OPERATING_COUNTRIES.some((country) => lower.includes(country));
}

function LocationSelect({ placeholder, value, otherLabel, onChange }) {
	function loadOptions(inputValue, callback) {
		if (!inputValue || inputValue.length < 3) {
			callback([]);
			return;
		}
		searchLocations(inputValue, otherLabel)
			.then((items) =>
				callback(
					items
						.filter((item) => item.source !== "google-places" || isInOperatingCountry(item.landmark))
						.map((item) => ({
							value: item.place_id || item.id,
							label: item.landmark,
							icon: item.icon,
							source: item.source,
						}))
				)
			)
			.catch(() => callback([]));
	}

	return (
		<AsyncSelect
			cacheOptions
			loadOptions={loadOptions}
			defaultOptions={false}
			styles={selectStyles}
			menuPortalTarget={document.body}
			placeholder={placeholder}
			noOptionsMessage={({ inputValue }) =>
				!inputValue || inputValue.length < 3
					? "Search by Airport Name / Cruise Terminal or Hotel Name"
					: "No locations found"
			}
			value={value}
			onChange={onChange}
			formatOptionLabel={({ label, icon }) => (
				<div className="d-flex align-items-center" style={{ gap: "8px" }}>
					{icon && <img src={icon} alt="" width={18} height={18} />}
					<span>{label}</span>
				</div>
			)}
		/>
	);
}

const TransferSearchForm = ({ onResults, onSearchStart, onSearchError }) => {
	const { searchData, setSearchData } = useTransferSearch();
	const agent = useSelector((state) => state.auth.auth) || {};
	const [searching, setSearching] = useState(false);

	const tomorrow = new Date();
	tomorrow.setDate(tomorrow.getDate() + 1);

	const isValidTime = ({ hour, minute } = {}) => Number.isInteger(hour) && Number.isInteger(minute);
	const isFormValid = () =>
		searchData.from && searchData.to && searchData.date && isValidTime(searchData.time) && searchData.passengers > 0;

	function updateField(field, value) {
		setSearchData((prev) => ({ ...prev, [field]: value }));
	}

	function handleDateTimeChange(date) {
		if (!date) {
			setSearchData((prev) => ({ ...prev, date: null, time: { hour: null, minute: null } }));
			return;
		}
		setSearchData((prev) => ({ ...prev, date, time: { hour: date.getHours(), minute: date.getMinutes() } }));
	}

	async function resolveAddress(place) {
		if (place.source === "google-places") {
			const postcode = await getPostalCode(place.value);
			return { address: postcode || place.label, type: postcode ? "postcode" : "address" };
		}
		return { address: place.label, type: "address" };
	}

	async function handleSearch() {
		if (!isFormValid()) return;
		setSearching(true);
		if (onSearchStart) onSearchStart();
		try {
			const [from, to] = await Promise.all([resolveAddress(searchData.from), resolveAddress(searchData.to)]);
			const payload = {
				from_address: from.address,
				from_type: from.type,
				to_address: to.address,
				to_type: to.type,
				no_of_pax: String(searchData.passengers),
				agent_id: agent.id,
			};
			const result = await searchTransfers(payload);
			onResults(result);
		} catch (error) {
			if (error.response) {
				formatApiError(error.response.data, "Unable to search transfers. Please try again.");
			} else {
				formatApiError(null, "Unable to reach the server. Please try again.");
			}
			if (onSearchError) onSearchError(error);
		} finally {
			setSearching(false);
		}
	}

	return (
		<div className="card">
			<div className="card-body">
				<h5 className="mb-3">
					<i className="fa fa-map-marker-alt text-primary me-2" />
					Search Transfers
				</h5>
				<div className="row g-3">
					<div className="col-md-6">
						<label className="form-label">Location (Pick-up from)</label>
						<LocationSelect
							placeholder="Search pick-up location"
							value={searchData.from}
							otherLabel={searchData.to?.label}
							onChange={(value) => updateField("from", value)}
						/>
					</div>
					<div className="col-md-6">
						<label className="form-label">Location (Drop-off to)</label>
						<LocationSelect
							placeholder="Search drop-off location"
							value={searchData.to}
							otherLabel={searchData.from?.label}
							onChange={(value) => updateField("to", value)}
						/>
					</div>
				</div>
				<div className="row g-3 align-items-end mt-1">
					<div className="col-md-5">
						<label className="form-label">Pickup Date &amp; Time</label>
						<div className="position-relative">
							<i className="fa fa-calendar-alt text-primary" style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", zIndex: 1 }} />
							<DatePicker
								className="form-control ps-5"
								wrapperClassName="w-100"
								selected={combineDateAndTime(searchData.date, searchData.time)}
								onChange={handleDateTimeChange}
								showTimeSelect
								timeIntervals={15}
								timeCaption="Time"
								placeholderText="Select date & time"
								dateFormat="dd MMM yyyy, h:mm aa"
								minDate={tomorrow}
								filterDate={isBookableDate}
							/>
						</div>
					</div>
					<div className="col-md-3">
						<label className="form-label">Passengers</label>
						<QuantityStepper value={searchData.passengers} min={1} onChange={(value) => updateField("passengers", value)} />
					</div>
					<div className="col-md-4">
						<button
							type="button"
							className="btn btn-primary w-100"
							disabled={!isFormValid() || searching}
							onClick={handleSearch}
						>
							<i className="fa fa-search me-2" />
							{searching ? "Searching..." : "Search"}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default TransferSearchForm;
